import { Loader2 } from "lucide-react";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { esAnaliticaNoDisponible } from "../dominio/analitica";
import type { Comercial } from "../dominio/tipos";

type Objetivo = {
  id: Identifier;
  sales_id: Identifier;
  mes: string;
  llamadas: number | null;
  citas: number | null;
  clientes: number | null;
  cobrado: number | null;
};
type Campo = "llamadas" | "citas" | "clientes" | "cobrado";
const CAMPOS: Campo[] = ["llamadas", "citas", "clientes", "cobrado"];
type Borrador = Record<string, Record<Campo, string>>;

const vacio = (): Record<Campo, string> => ({
  llamadas: "",
  citas: "",
  clientes: "",
  cobrado: "",
});
const aTexto = (v: number | null | undefined) => (v == null ? "" : String(v));
const aNumero = (s: string): number | null => {
  const n = Number(s.replace(",", "."));
  return s.trim() === "" || !Number.isFinite(n) || n < 0 ? null : n;
};
const mesAnterior = (mes: string) => {
  const [a, m] = mes.split("-").map(Number);
  const d = new Date(a, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * Objetivos del mes por persona (47, Robert, 31/10/2026): llamadas, consultorías agendadas,
 * clientes nuevos y cobrado. El Inicio enseña cómo va cada uno (y el equipo, la suma). Solo
 * Jan y Robert los ponen; cada setter ve los suyos en su Inicio.
 */
export const ObjetivosEquipo = ({
  mes,
  usuarios,
}: {
  /** «aaaa-mm». */
  mes: string;
  usuarios: Comercial[];
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const t = (k: string, o?: object) =>
    translate(`nevora.equipo.objetivos.${k}`, o);
  const filtro = (m: string) => ({
    filter: { mes: `${m}-01` },
    pagination: { page: 1, perPage: 200 },
    sort: { field: "id", order: "ASC" as const },
  });
  const { data, error, refetch } = useGetList<Objetivo>(
    "objetivos",
    filtro(mes),
    { retry: false },
  );
  const { data: anteriores } = useGetList<Objetivo>(
    "objetivos",
    filtro(mesAnterior(mes)),
    { retry: false, enabled: !error },
  );
  const [borrador, setBorrador] = useState<Borrador>({});
  const [guardando, setGuardando] = useState(false);

  const desde = (lista: Objetivo[] | undefined): Borrador =>
    Object.fromEntries(
      usuarios.map((u) => {
        const o = lista?.find((x) => String(x.sales_id) === String(u.id));
        return [
          String(u.id),
          o
            ? {
                llamadas: aTexto(o.llamadas),
                citas: aTexto(o.citas),
                clientes: aTexto(o.clientes),
                cobrado: aTexto(o.cobrado),
              }
            : vacio(),
        ];
      }),
    );

  useEffect(() => {
    if (data) setBorrador(desde(data));
    // Solo al cambiar de mes o de datos guardados.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, usuarios.length]);

  if (error && esAnaliticaNoDisponible(error))
    return (
      <p className="text-sm text-muted-foreground">{t("no_disponible")}</p>
    );

  const guardar = async () => {
    setGuardando(true);
    try {
      for (const u of usuarios) {
        const fila = borrador[String(u.id)] ?? vacio();
        const valores = Object.fromEntries(
          CAMPOS.map((c) => [c, aNumero(fila[c])]),
        ) as Record<Campo, number | null>;
        const hay = CAMPOS.some((c) => valores[c] != null);
        const existente = data?.find(
          (x) => String(x.sales_id) === String(u.id),
        );
        if (existente && !hay) {
          await dataProvider.delete("objetivos", {
            id: existente.id,
            previousData: existente,
          });
        } else if (existente) {
          await dataProvider.update("objetivos", {
            id: existente.id,
            data: valores,
            previousData: existente,
          });
        } else if (hay) {
          await dataProvider.create("objetivos", {
            data: { sales_id: u.id, mes: `${mes}-01`, ...valores },
          });
        }
      }
      notify("nevora.equipo.objetivos.guardados", { type: "success" });
      await refetch();
    } catch (e) {
      notify(e instanceof Error ? e.message : "ra.notification.http_error", {
        type: "error",
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="objetivos-equipo">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-left">
          <thead>
            <tr>
              <th className="pb-2 text-xs font-medium text-muted-foreground">
                {t("persona")}
              </th>
              {CAMPOS.map((c) => (
                <th
                  key={c}
                  className="pb-2 pl-2 text-right text-xs font-medium text-muted-foreground"
                >
                  {t(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {usuarios.map((u) => {
              const fila = borrador[String(u.id)] ?? vacio();
              return (
                <tr key={String(u.id)}>
                  <td className="py-2 text-sm">{`${u.first_name} ${u.last_name}`}</td>
                  {CAMPOS.map((c) => (
                    <td key={c} className="py-1.5 pl-2">
                      <Input
                        type="number"
                        min={0}
                        step={c === "cobrado" ? 100 : 1}
                        inputMode="decimal"
                        aria-label={`${t(c)} · ${u.first_name}`}
                        className="ml-auto h-8 w-24 text-right tabular-nums"
                        value={fila[c]}
                        onChange={(e) =>
                          setBorrador({
                            ...borrador,
                            [String(u.id)]: { ...fila, [c]: e.target.value },
                          })
                        }
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={guardar} disabled={guardando || !data}>
          {guardando ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
          {t("guardar")}
        </Button>
        <Button
          variant="outline"
          disabled={!anteriores?.length}
          onClick={() => setBorrador(desde(anteriores))}
        >
          {t("copiar_anterior")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t("ayuda")}</p>
    </div>
  );
};
